import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { MultiSavingsPlan, SavingsPlanFacade } from '@zero/saving-plans/domain';
import { MultiOrderConsentComponent } from '@zero/shared/ui-web-components';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { commonRoutes } from 'constants/web-app-constants';
import { combineLatest, filter, take } from 'rxjs';
import { SavingsPlanConfirmComponent } from '../../components/savings-plan-confirm/savings-plan-confirm.component';
import { SavingsPlanCreateComponent } from '../../components/savings-plan-create/savings-plan-create.component';
import { SavingsPlanStrategyRevokeModalComponent } from '../../components/savings-plan-create/savings-plan-strategy-revoke-modal/savings-plan-strategy-revoke-modal.component';
import { CurrentStep } from '../../util/current-step';

@Component({
  templateUrl: './savings-plan-create-page.component.html',
  imports: [AsyncPipe, SavingsPlanCreateComponent, SavingsPlanConfirmComponent],
})
export class SavingsPlanCreatePageComponent implements OnInit, OnDestroy {
  public readonly facade = inject(SavingsPlanFacade);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #ngbModal = inject(NgbModal);
  readonly #route = inject(ActivatedRoute);
  readonly #modalOptions: NgbModalOptions = { centered: true, size: 'lg', backdrop: 'static', keyboard: false };

  public readonly STEPS = CurrentStep;
  public step = CurrentStep.CREATE;

  ngOnInit() {
    this.#customerStateFacade.loadCustomerBaseData();
    this.facade.loadOptions();
    this.facade.execDates$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((execDates) => this.facade.changeExecDate(execDates[0]));
    this.facade.intervals$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((intervals) => this.facade.changeInterval(intervals[0]));
    this.facade.setDynamization(null);
    this.#listenToMultiOrderConsent();
    this.#listenToUrlParams();
    this.#listenToStrategyChange();
    this.#listenToSavingsPlansCreated();
  }

  ngOnDestroy() {
    this.facade.clearForm();
  }

  public handleNoWallet() {
    this.#router.navigate([commonRoutes.postlogin.verwaltung], {
      queryParams: {
        section: 'krypto-wallet',
      },
    });
  }

  #listenToMultiOrderConsent = () => {
    combineLatest({
      isMultiOrder: this.facade.isMultiSavingsPlanOrder$,
      customerBaseData: this.#customerStateFacade.customerBaseData$,
    })
      .pipe(
        filter(({ isMultiOrder, customerBaseData }) => isMultiOrder && customerBaseData && !customerBaseData.multiOrderConfirmed),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        const ref = this.#ngbModal.open(MultiOrderConsentComponent, this.#modalOptions);
        ref.componentInstance.type = 'savingsPlan';
      });
  };

  #listenToUrlParams = () => {
    this.#route.queryParams
      .pipe(filter((params) => !!params['isin'] && !!params['share'] && !isNaN(params['totalAmount'])))
      .subscribe(({ isin, share, totalAmount }) => this.#extractFromUrl(isin, share, totalAmount));

    combineLatest([this.#route.params, this.#route.queryParams])
      .pipe(filter(([params]) => !!params['isin']))
      .subscribe(([params, queryParams]) =>
        this.facade.addAssetsToForm(queryParams['segment'] ? Number(queryParams['segment']) : 0, [
          {
            isin: params['isin'],
            share: queryParams['amount'] ? Number(queryParams['amount']) : 0,
          } as MultiSavingsPlan,
        ]),
      );
  };

  #listenToStrategyChange = () => {
    combineLatest({
      isStrategy: this.facade.multiSavingsPlanIsStrategy$,
      totalAmount: this.facade.multiSavingsPlanTotalAmount$,
      multiSavingsPlansWithAssetsNoSavings: this.facade.multiSavingsPlansWithAssetsNoSavings$,
    })
      .pipe(
        filter(
          ({ isStrategy, totalAmount, multiSavingsPlansWithAssetsNoSavings }) =>
            (isStrategy && multiSavingsPlansWithAssetsNoSavings.length > 0) || (isStrategy === false && totalAmount > 0),
        ),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ isStrategy, multiSavingsPlansWithAssetsNoSavings, totalAmount }) => {
        if (isStrategy && multiSavingsPlansWithAssetsNoSavings?.length) {
          this.facade.removeSavingsPlansFromForm(multiSavingsPlansWithAssetsNoSavings);

          const modal = this.#ngbModal.open(SavingsPlanStrategyRevokeModalComponent, this.#modalOptions);
          modal.componentInstance.type = 'noSavings';
        } else if (!isStrategy && totalAmount > 0) {
          this.#ngbModal.open(SavingsPlanStrategyRevokeModalComponent, this.#modalOptions);
        }
      });
  };

  #listenToSavingsPlansCreated = () => {
    this.facade.savingsPlansCreated$
      .pipe(
        filter((created) => created),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#router.navigate([commonRoutes.postlogin.savings_plans]));
  };

  #extractFromUrl = (isin: string, share: string, totalAmount: string) => {
    const separator = ',';
    this.facade.addAssetsToForm(
      Number(totalAmount),
      isin
        .split(separator)
        .map(
          (isin: string, i: number) =>
            ({
              isin: isin ? isin.trim() : '',
              share: share ? Number(share.split(separator)[i]?.trim()) : null,
            }) as MultiSavingsPlan,
        )
        .filter(({ isin }) => isin.length === 12),
    );
  };

  public goBack() {
    this.facade.clearMultiSavingsPlanError();
    this.step = CurrentStep.CREATE;
  }
}
